import { useState, useMemo } from 'react';
import {formatISO, subDays, subMonths, subYears} from 'date-fns';

export const useDateRange = () => {
    const [selectedRange, setSelectedRange] = useState('month');
    const [customRange, setCustomRange] = useState<{ start: Date; end: Date }>();

    const getDateRange = useMemo(() => {
        const now = new Date();

        const before = new Date(now);
        before.setDate(before.getDate() - 1);
        before.setHours(23, 59, 59, 999);

        let start: Date;
        let end: Date = before;

        switch (selectedRange) {
            case 'week':
                start = subDays(before, 6);
                break;
            case 'month':
                start = subMonths(before, 1);
                break;
            case 'quarter':
                start = subMonths(before, 3);
                break;
            case 'year':
                start = subYears(before, 1);
                break;
            case 'custom':
                if (customRange && customRange.start && customRange.end) {
                    start = customRange.start;
                    end = customRange.end;
                } else {
                    start = subMonths(before, 1);
                }
                break;
            default:
                start = subMonths(before, 1);
        }

        if (selectedRange !== 'custom') {
            start.setHours(0, 0, 0, 0);
        }

        return {
            start: formatISO(start, { representation: 'date' }),
            end: formatISO(end, { representation: 'date' }),
            startDate: start,
            endDate: end,
            rangeType: selectedRange,
        };
    }, [selectedRange, customRange]);

    const updateRange = (range: string) => {
        setSelectedRange(range);
        if (range !== 'custom') {
            const now = new Date();
            const before = new Date(now);
            before.setDate(before.getDate() - 1);
            before.setHours(23, 59, 59, 999);
            let newStart: Date;

            switch (range) {
                case 'week':
                    newStart = subDays(before, 6);
                    break;
                case 'month':
                    newStart = subMonths(before, 1);
                    break;
                case 'quarter':
                    newStart = subMonths(before, 3);
                    break;
                case 'year':
                    newStart = subYears(before, 1);
                    break;
                default:
                    newStart = subMonths(before, 1);
            }

            newStart.setHours(0, 0, 0, 0);

            setCustomRange({
                start: newStart,
                end: before,
            });
        }
    };

    const updateCustomRange = (start: Date, end: Date) => {
        setSelectedRange('custom');
        const normalizedStart = new Date(start);
        normalizedStart.setHours(0, 0, 0, 0);

        const normalizedEnd = new Date(end);
        normalizedEnd.setHours(23, 59, 59, 999);
        setCustomRange({
            start: normalizedStart,
            end: normalizedEnd,
        });
    };

    return {
        selectedRange,
        customRange,
        dateRange: getDateRange,
        updateRange,
        updateCustomRange,
        rangeType: selectedRange,
    };
};